import React, { useState, useEffect } from 'react';
import { adminServices, ROLE_PRESETS, STAFF_PERMISSION_DEFINITIONS, payRatesForRole } from '../services/adminServices';
import AuditorProfile from './AuditorProfile';
import { useUser } from '../UserContext';

function readPayRatesForm(staff) {
  const raw = staff?.payRates;
  const entry = Array.isArray(raw) ? raw[raw.length - 1] : raw;
  if (!entry || typeof entry !== 'object') {
    return payRatesForRole(staff?.role || 'ACCREDITOR');
  }
  return {
    accreditorFeeGbp: entry.accreditorFeeGbp ?? '',
    adminFeeGbp: entry.adminFeeGbp ?? '',
    adminFeeRecipientUid: entry.adminFeeRecipientUid || 'self',
    effectiveFrom: entry.effectiveFrom || new Date().toISOString().slice(0, 10),
  };
}

const StaffManagement = () => {
  const { user } = useUser();
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editorTab, setEditorTab] = useState('permissions');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    displayName: '',
    role: 'ACCREDITOR',
    isApproved: false,
  });
  const [inviteError, setInviteError] = useState('');
  const [payRatesForm, setPayRatesForm] = useState(null);

  useEffect(() => {
    if (selectedStaff) {
      setPayRatesForm(readPayRatesForm(selectedStaff));
    } else {
      setPayRatesForm(null);
    }
  }, [selectedStaff?.id]);

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    const data = await adminServices.getAllStaff();
    setStaff(data);
  };

  const handleRoleChange = (role) => {
    const preset = ROLE_PRESETS[role] || {};
    setSelectedStaff({
      ...selectedStaff,
      role,
      permissions: { ...preset }
    });
  };

  const togglePermission = (key) => {
    setSelectedStaff({
      ...selectedStaff,
      permissions: {
        ...selectedStaff.permissions,
        [key]: !selectedStaff.permissions[key]
      }
    });
  };

  const saveStaff = async () => {
    setIsSaving(true);
    try {
      const accreditorFee = Number(payRatesForm?.accreditorFeeGbp);
      const adminFee = Number(payRatesForm?.adminFeeGbp);
      const payRates = {
        accreditorFeeGbp: Number.isFinite(accreditorFee) ? Math.round(accreditorFee) : null,
        adminFeeGbp: Number.isFinite(adminFee) ? Math.round(adminFee) : null,
        adminFeeRecipientUid: payRatesForm?.adminFeeRecipientUid || 'self',
        effectiveFrom: payRatesForm?.effectiveFrom || new Date().toISOString().slice(0, 10),
      };
      await adminServices.updateStaffMember(selectedStaff.id, {
        role: selectedStaff.role,
        permissions: selectedStaff.permissions,
        isApproved: selectedStaff.isApproved,
        payRates,
      }, user.displayName);
      await loadStaff();
      setSelectedStaff(null);
    } catch (err) {
      alert("Error saving: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setIsInviting(true);
    try {
      await adminServices.inviteStaffMember(
        {
          email: inviteForm.email,
          displayName: inviteForm.displayName,
          role: inviteForm.role,
          isApproved: inviteForm.isApproved,
        },
        user.displayName || user.email
      );
      setInviteForm({
        email: '',
        displayName: '',
        role: 'ACCREDITOR',
        isApproved: false,
      });
      setShowInviteForm(false);
      await loadStaff();
    } catch (err) {
      setInviteError(err?.message || 'Could not create invitation.');
    } finally {
      setIsInviting(false);
    }
  };

  const staffStatusLabel = (member) => {
    if (member.pendingInvite) return 'Invited (not signed in)';
    if (member.isApproved) return 'Approved';
    return 'Pending approval';
  };

  return (
    <div className="staff-management-full-width">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
        <h3 style={{ color: 'white', margin: 0 }}>IFIP Team Members</h3>
        <button
          type="button"
          className="btn-activate"
          onClick={() => {
            setShowInviteForm((open) => !open);
            setInviteError('');
          }}
          style={{ minWidth: '180px' }}
        >
          {showInviteForm ? 'Cancel invite' : 'Invite team member'}
        </button>
      </div>

      {showInviteForm && (
        <form
          onSubmit={handleInvite}
          style={{
            background: '#1e293b',
            border: '1px solid rgba(19, 138, 138, 0.35)',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h4 style={{ color: '#fff', marginTop: 0, marginBottom: '8px' }}>Pre-register staff</h4>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 0, marginBottom: '20px', lineHeight: 1.5 }}>
            Create a staff record before the person signs in with Google. When they first log in with the matching
            {' '}<strong>@ifip.group</strong> account, their role and permissions are applied automatically.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.8rem', color: '#94a3b8' }}>
              Email address
              <input
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="name@ifip.group"
                style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.8rem', color: '#94a3b8' }}>
              Display name (optional)
              <input
                type="text"
                value={inviteForm.displayName}
                onChange={(e) => setInviteForm({ ...inviteForm, displayName: e.target.value })}
                placeholder="Jane Accreditor"
                style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.8rem', color: '#94a3b8' }}>
              Role template
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
              >
                <option value="ACCREDITOR">Accreditor (Reviewer)</option>
                <option value="SENIOR_ACCREDITOR">Senior Accreditor (Lead)</option>
                <option value="SUPER_ADMIN">Super Admin (Owner)</option>
              </select>
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: 'white', marginBottom: '16px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={inviteForm.isApproved}
              onChange={(e) => setInviteForm({ ...inviteForm, isApproved: e.target.checked })}
            />
            Approve immediately on first sign-in (skip pending onboarding queue)
          </label>
          {inviteError && (
            <p style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '12px' }}>{inviteError}</p>
          )}
          <button type="submit" className="btn-activate" disabled={isInviting} style={{ minWidth: '160px' }}>
            {isInviting ? 'Creating…' : 'Create invitation'}
          </button>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Role</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {staff.map(s => (
            <React.Fragment key={s.id}>
              <tr className={selectedStaff?.id === s.id ? 'active' : ''}>
                <td>
                  <strong style={{ color: 'white' }}>{s.displayName}</strong><br/>
                  <small style={{ opacity: 0.6 }}>{s.email}</small>
                </td>
                <td>
                  <span className={`status-dot ${s.isApproved ? 'active' : 'inactive'}`}></span>
                  {staffStatusLabel(s)}
                </td>
                <td style={{ fontSize: '0.75rem' }}>{s.role?.replace('_', ' ') || 'No Role'}</td>
                <td>
                  <div className="staff-action-buttons">
                    <button 
                      onClick={() => { 
                        if (selectedStaff?.id === s.id && editorTab === 'permissions') setSelectedStaff(null);
                        else { setSelectedStaff(s); setEditorTab('permissions'); }
                      }} 
                      className="btn-nudge"
                    >
                      Permissions
                    </button>
                    <button 
                      onClick={() => { 
                        if (selectedStaff?.id === s.id && editorTab === 'profile') setSelectedStaff(null);
                        else { setSelectedStaff(s); setEditorTab('profile'); }
                      }} 
                      className="btn-nudge" 
                      style={{ background: 'rgba(255,255,255,0.05)' }}
                    >
                      Profile
                    </button>
                  </div>
                </td>
              </tr>
              
              {selectedStaff?.id === s.id && (
                <tr className="staff-expansion-row">
                  <td colSpan="4" style={{ padding: '20px', background: 'rgba(0,0,0,0.2)' }}>
                    <div className="editor-card-expansion" style={{ background: '#1e293b', padding: '30px', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', gap: '20px', marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <button 
                          onClick={() => setEditorTab('permissions')}
                          style={{ 
                            background: 'none', border: 'none', padding: '10px 0', cursor: 'pointer',
                            color: editorTab === 'permissions' ? '#138a8a' : '#666',
                            borderBottom: editorTab === 'permissions' ? '2px solid #138a8a' : 'none',
                            fontSize: '0.8rem', fontWeight: 'bold'
                          }}
                        >
                          Access & Roles
                        </button>
                        <button 
                          onClick={() => setEditorTab('profile')}
                          style={{ 
                            background: 'none', border: 'none', padding: '10px 0', cursor: 'pointer',
                            color: editorTab === 'profile' ? '#138a8a' : '#666',
                            borderBottom: editorTab === 'profile' ? '2px solid #138a8a' : 'none',
                            fontSize: '0.8rem', fontWeight: 'bold'
                          }}
                        >
                          Professional Profile
                        </button>
                      </div>

                      {editorTab === 'permissions' ? (
                        <>
                          <div className="editor-row" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Account Status</label>
                            <button 
                              className={selectedStaff.isApproved ? "btn-danger-text" : "btn-activate-text"}
                              onClick={() => setSelectedStaff({...selectedStaff, isApproved: !selectedStaff.isApproved})}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              {selectedStaff.isApproved ? "Revoke Access" : "Approve Account"}
                            </button>
                          </div>

                          <div className="editor-row" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '8px' }}>Role Template</label>
                            <select 
                              value={selectedStaff.role || ""} 
                              onChange={(e) => handleRoleChange(e.target.value)}
                              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
                            >
                              <option value="">Custom / No Role</option>
                              <option value="ACCREDITOR">Accreditor (Reviewer)</option>
                              <option value="SENIOR_ACCREDITOR">Senior Accreditor (Lead)</option>
                              <option value="SUPER_ADMIN">Super Admin (Owner)</option>
                            </select>
                          </div>

                          <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '12px' }}>
                              Pay rates (GBP per finalised accreditation)
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
                                Accreditor fee
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={payRatesForm?.accreditorFeeGbp ?? ''}
                                  onChange={(e) => setPayRatesForm({ ...payRatesForm, accreditorFeeGbp: e.target.value })}
                                  style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
                                Admin fee
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={payRatesForm?.adminFeeGbp ?? ''}
                                  onChange={(e) => setPayRatesForm({ ...payRatesForm, adminFeeGbp: e.target.value })}
                                  style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
                                Admin fee payee
                                <select
                                  value={payRatesForm?.adminFeeRecipientUid || 'self'}
                                  onChange={(e) => setPayRatesForm({ ...payRatesForm, adminFeeRecipientUid: e.target.value })}
                                  style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
                                >
                                  <option value="self">Same as accreditor</option>
                                  {staff
                                    .filter((member) => member.id && !String(member.id).includes('@'))
                                    .map((member) => (
                                      <option key={member.id} value={member.id}>
                                        {member.displayName || member.email}
                                      </option>
                                    ))}
                                </select>
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
                                Effective from
                                <input
                                  type="date"
                                  value={payRatesForm?.effectiveFrom || ''}
                                  onChange={(e) => setPayRatesForm({ ...payRatesForm, effectiveFrom: e.target.value })}
                                  style={{ background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px' }}
                                />
                              </label>
                            </div>
                            <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '12px 0 0', lineHeight: 1.45 }}>
                              Applied at finalise when this person is the assigned accreditor. Leave blank amounts to
                              accrue as pending until rates are set.
                            </p>
                          </div>

                          <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '12px' }}>Granular Access</label>
                          <div className="permissions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                            {STAFF_PERMISSION_DEFINITIONS.map(({ key, label, description }) => (
                              <label
                                key={key}
                                title={description}
                                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.75rem', color: 'white', cursor: 'pointer' }}
                              >
                                <input 
                                  type="checkbox" 
                                  checked={selectedStaff.permissions?.[key] || false} 
                                  onChange={() => togglePermission(key)}
                                  style={{ marginTop: '2px' }}
                                />
                                <span>
                                  <strong style={{ display: 'block' }}>{label}</strong>
                                  <span style={{ color: '#94a3b8', lineHeight: 1.4 }}>{description}</span>
                                </span>
                              </label>
                            ))}
                          </div>

                          <div className="editor-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setSelectedStaff(null)} className="btn-secondary" style={{ width: '150px' }}>Close</button>
                            <button onClick={saveStaff} className="btn-activate" disabled={isSaving} style={{ width: '200px' }}>
                              {isSaving ? "Saving..." : "Save Permissions"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <AuditorProfile 
                          targetStaff={selectedStaff} 
                          onActionComplete={() => {
                            loadStaff();
                            setSelectedStaff(null);
                          }} 
                        />
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
     </table>
    </div>
  );
};

export default StaffManagement;
